"use client"

import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { createAccountAsync } from "./registerSlice"
import { useNavigate } from "react-router-dom"
import {
  User,
  MapPin,
  CreditCard,
  Users,
  GraduationCap,
  Upload,
  Check,
  AlertCircle,
  Calendar,
  Fingerprint,
  X,
  ArrowLeft,
} from "lucide-react"


const RegistrationForm = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [url, setUrl] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  // Upload image to Flask backend
  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append("file", file)
    setIsSubmitting(true)
    try {
      const res = await fetch("/upload", {
        method: "POST",
        body: formData,
      })
      const data = await res.json()
      if (data.filename) {
        setUrl(data.filename)
      } else {
        setUrl(null)
        alert("Image upload failed")
      }
    } catch (err) {
      setUrl(null)
      alert("Image upload failed")
    }
    setIsSubmitting(false)
  }

  const onSubmit = (data) => {
    setIsSubmitting(true)
    const inputs = Array.from({ length: 10 }).map((_, index) => {
      const inputs = `input${index}`;
      return data[inputs];
    });
    const inputsअंक = Array.from({ length: 10 }).map((_, index) => {
      const inputsअंक = `inputअंक${index}`;
      return data[inputsअंक];
    });

    for (let i = 0; i < inputs.length; i++) {
      delete data[`input${i}`];
      delete data[`inputअंक${i}`];
    }
    
    // Auto-fill numeric fields with default values if empty
    const processedData = {
      ...data,
      // Auto-fill Aadhaar with 12 zeros if empty
      आधार_नं: data.आधार_नं || "000000000000",
      // Auto-fill mobile numbers with 10 zeros if empty (except required ones)
      उत्तराधिकारी_मो: data.उत्तराधिकारी_मो || "0000000000",
      // Auto-fill bank account number with zeros if empty
      खाता_नं: data.खाता_नं || "00000000000000000",
      // Auto-fill other numeric fields
      क्रमांक: data.क्रमांक || "0000",
      परिवार_आईडी: data.परिवार_आईडी || "000000000000",
      वि: data.वि || "-",
      लो: data.लो || "-",
      // Auto-fill attendance marks with 0 if empty
      ...Array.from({ length: 10 }).reduce((acc, _, index) => {
        acc[`उपस्थिति_अंक_${index}`] = data[`उपस्थिति_अंक_${index}`] || "0";
        return acc;
      }, {}),
      उपस्थित: inputs,
      inputsअंक: inputsअंक,
    };
    
    processedData["imageURL"] =
      url ||
      "https://imgs.search.brave.com/oB6fgT45DC10B0RQfk3kTBtZ0W-2p7udZUxPnfvKT3M/rs:fit:860:0:0/g:ce/aHR0cHM6Ly90My5m/dGNkbi5uZXQvanBn/LzA0LzYyLzkzLzY2/LzM2MF9GXzQ2Mjkz/NjY4OV9CcEVFY3hm/Z011WVBmVGFJQU9D/MXRDRHVybXNubzdT/cC5qcGc";
    setUrl(null);
    dispatch(createAccountAsync(processedData));
    reset();
    setIsSubmitting(false)
    alert("प्रोफाइल सफलतापूर्वक बनाई गई");
  };

  const FormSection = ({
    title,
    icon: Icon,
    children,
  }) => (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </section>
  )

  const InputField = ({
    label,
    name,
    type = "text",
    required = false,
    placeholder,
    validation = {},
    ...props
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          {...register(name, { required: required ? `${label} आवश्यक है` : false, ...validation })}
          placeholder={placeholder}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
          {...props}
        />
      ) : (
        <input
          type={type}
          {...register(name, { required: required ? `${label} आवश्यक है` : false, ...validation })}
          placeholder={placeholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          {...props}
        />
      )}
      {errors[name] && (
        <p className="text-red-500 text-sm flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {errors[name]?.message}
        </p>
      )}
    </div>
  )

  const CheckboxField = ({ label, name }) => (
    <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200 border border-gray-100">
      <input
        type="checkbox"
        {...register(name)}
        className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
      />
      <span className="text-gray-700 font-medium select-none">{label}</span>
    </label>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                aria-label="वापस जाएं"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">वापस</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">नया प्रोफाइल बनाएं</h1>
                <p className="text-sm text-gray-600 hidden sm:block">कृपया सभी आवश्यक जानकारी सही तरीके से भरें</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Photo Upload Section */}
          <FormSection title="फोटो अपलोड करें" icon={Upload}>
            <div className="flex justify-center items-center gap-6 w-full">
              {url ? (
                <div className="relative group">
                  <img
                    src={url || "/placeholder.svg"}
                    alt="प्रोफाइल फोटो"
                    className="w-32 h-32 object-cover rounded-xl border-2 border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => setUrl(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600 transition-colors duration-200 shadow-lg"
                    aria-label="फोटो हटाएं"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full max-w-md h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group">
                  <Upload className="text-gray-400 text-3xl mb-3 group-hover:text-gray-500 transition-colors duration-200" />
                  <p className="text-sm text-gray-600 text-center px-4">
                    <span className="font-medium">फोटो अपलोड करने के लिए क्लिक करें</span>
                    <br />
                    <span className="text-xs text-gray-500">PNG, JPG, JPEG (अधिकतम 5MB)</span>
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    aria-label="फोटो चुनें"
                  />
                </label>
              )}
            </div>
          </FormSection>

          {/* Basic Information */}
          <FormSection title="बुनियादी जानकारी" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <InputField label="क्रमांक" name="क्रमांक" placeholder="क्रमांक दर्ज करें" />
              <InputField label="नियुक्ति दिनांक" name="नियुक्ति_दिनांक" type="date" />
              <InputField label="नाम" name="नाम" placeholder="पूरा नाम दर्ज करें" />
              <InputField label="पिताश्री" name="पिताश्री" placeholder="पिता का नाम दर्ज करें" />
              <InputField label="दादाश्री" name="दादाश्री" placeholder="दादा का नाम दर्ज करें" />
              <InputField label="जाति" name="जाति" placeholder="जाति दर्ज करें" />
              <InputField label="व्यवसाय" name="व्यवसाय" placeholder="व्यवसाय दर्ज करें" />
              <InputField label="जन्म दिनांक" name="जन्म_दिनांक" type="date" />
            </div>

            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">अतिरिक्त जानकारी</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CheckboxField label="नियुक्ति पत्र गुम हो गया" name="नियुक्ति_पत्र_गुम" />
                <CheckboxField label="नियुक्ति नहीं है" name="नियुक्ति_नहीं" />
              </div>
            </div>

            <div className="mt-6">
              <InputField label="अभिमत" name="अभिमत" type="textarea" placeholder="अभिमत दर्ज करें" />
            </div>
          </FormSection>

          {/* Address Information */}
          <FormSection title="पता विवरण" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <InputField label="जिला" name="जिला" placeholder="जिला दर्ज करें" />
              <InputField label="तहसील" name="तहसील" placeholder="तहसील दर्ज करें" />
              <InputField label="उपतहसील" name="उपतहसील" placeholder="उपतहसील दर्ज करें" />
              <InputField label="जनपद" name="जनपद" placeholder="जनपद दर्ज करें" />
              <InputField label="ग्राम" name="ग्राम" placeholder="गांव का नाम दर्ज करें" />
              <InputField label="पंचायत" name="पंचायत" placeholder="पंचायत दर्ज करें" />
              <InputField label="पोस्ट" name="पोस्ट" placeholder="पोस्ट ऑफिस दर्ज करें" />
              <InputField label="थाना" name="थाना" placeholder="थाना दर्ज करें" />
            </div>
          </FormSection>

          {/* Identity Information */}
          <FormSection title="पहचान दस्तावेज" icon={CreditCard}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <InputField
                label="आधार नं."
                name="आधार_नं"
                placeholder="आधार नंबर दर्ज करें"
                maxLength={12}
                validation={{
                  pattern: {
                    value: /^\d{12}$/,
                    message: "आधार नंबर 12 अंकों का होना चाहिए",
                  },
                }}
              />
              <InputField label="परिवार आईडी" name="परिवार_आईडी" placeholder="परिवार आईडी दर्ज करें" />
              <InputField label="वि." name="वि" placeholder="वि. नंबर दर्ज करें" />
              <InputField label="लो." name="लो" placeholder="लो. नंबर दर्ज करें" />
            </div>

            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">अतिरिक्त दस्तावेज</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InputField
                  label="पेनकार्ड"
                  name="पेनकार्ड"
                  placeholder="पेनकार्ड नंबर दर्ज करें"
                  maxLength={10}
                  validation={{
                    pattern: {
                      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                      message: "मान्य पेनकार्ड नंबर दर्ज करें (ABCDE1234F)",
                    },
                  }}
                />
              </div>
            </div>
          </FormSection>

          {/* Family Information */}
          <FormSection title="पारिवारिक विवरण" icon={Users}>
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">उत्तराधिकारी जानकारी</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-6">
                  <InputField
                    label="पारिवारिक सदस्य पटेल के उत्तराधिकारी का नाम"
                    name="पारिवारिक_सदस्य_पटेल"
                    placeholder="उत्तराधिकारी का नाम दर्ज करें"
                  />
                  <InputField
                    label="उत्तराधिकारी का नाम"
                    name="उत्तराधिकारी_नाम"
                    placeholder="उत्तराधिकारी का पूरा नाम"
                  />
                  <InputField
                    label="मोबाइल नंबर*"
                    name="उत्तराधिकारी_मो"
                    type="tel"
                    placeholder="मोबाइल नंबर दर्ज करें"
                    maxLength={10}
                    
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">रिश्ता (नियुक्त पटेल के साथ)</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <CheckboxField label="पुत्र" name="पुत्र" />
                  <CheckboxField label="पुत्री" name="पुत्री" />
                  <CheckboxField label="भाई" name="भाई" />
                  <CheckboxField label="पत्नी" name="पत्नी" />
                  <CheckboxField label="पौत्र" name="पौत्र" />
                  <CheckboxField label="नियुक्ति पटेल" name="नियुक्ति पटेल" />
                </div>
              </div>
            </div>
          </FormSection>

          {/* Education and Bank Information */}
          <FormSection title="शिक्षा और बैंक विवरण" icon={GraduationCap}>
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">शैक्षणिक जानकारी</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField label="कक्षा" name="शिक्षा_कक्षा" placeholder="शिक्षा स्तर दर्ज करें" />
                  <InputField label="शिक्षा_डिग्री" name="शिक्षा_डिग्री" placeholder="डिग्री का नाम दर्ज करें" />
                  <InputField
                    label="मोबाइल नंबर*"
                    name="मो"
                    type="tel"
                    placeholder="मोबाइल नंबर दर्ज करें"
                    maxLength={10}
                    
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">बैंक विवरण</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  <InputField label="बैंक का नाम" name="बैंक_का_नाम" placeholder="बैंक का नाम दर्ज करें" />
                  <InputField label="खाता नं." name="खाता_नं" placeholder="खाता संख्या दर्ज करें" />

                  <InputField
                    label="मोबाइल नंबर*"
                    name="मो_बैंक"
                    type="tel"
                    placeholder="मोबाइल नंबर दर्ज करें"
                    maxLength={10}
                    
                  />
                  <InputField label="IFSC कोड" name="IFSC" placeholder="IFSC कोड दर्ज करें" />
                </div>
              </div>
            </div>
          </FormSection>

          {/* Attendance Records */}
          <FormSection title="उपस्थिति रिकॉर्ड" icon={Fingerprint}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 10 }).map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    उपस्थिति {index + 1}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">अंक</label>
                      <input
                        type="number"
                        {...register(`उपस्थिति_अंक_${index}`)}
                        placeholder="5 अंक"
                        min="0"
                        max="99999"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">दिनांक</label>
                      <input
                        type="date"
                        {...register(`उपस्थिति_दिनांक_${index}`)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </FormSection>

          {/* Submit Button */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col items-center space-y-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full max-w-md py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    प्रोफाइल बनाई जा रही है...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    प्रोफाइल बनाएं
                  </>
                )}
              </button>
              <p className="text-sm text-gray-500 text-center">सबमिट करने से पहले कृपया सभी जानकारी की जांच कर लें</p>
            </div>
          </div>
        </form>
      </main>
    </div>
  )
}

export default RegistrationForm